import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { OpportunityDetail, OpportunityStatus, OpportunityType, UpdateOpportunityRequest } from '../../../models/opportunity.model';
import { OfficeLocationService } from '../../../services/officeLocationService';
import { STATUS_OPTIONS, TYPE_LABEL, TYPE_OPTIONS } from '../constants/opportunity.constants';
import { useUpdateOpportunity } from '../hooks/useOpportunityMutations';

const officeLocationSvc = new OfficeLocationService();

interface InfoPanelProps {
  opportunity: OpportunityDetail;
  isArchived: boolean;
  createdBy: string;
}

export const OpportunityInfoPanel = ({ opportunity, isArchived, createdBy }: InfoPanelProps) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(opportunity.title);
  const [description, setDescription] = useState(opportunity.description ?? '');
  const [type, setType] = useState(String(opportunity.opportunityType));
  const [status, setStatus] = useState(String(opportunity.opportunityStatus));
  const [availDate, setAvailDate] = useState<Date | null>(
    opportunity.requiredAvailabilityDate ? new Date(opportunity.requiredAvailabilityDate) : null,
  );
  const [commitment, setCommitment] = useState<number | string>(opportunity.timeCommitmentPercent ?? '');
  const [availWeight, setAvailWeight] = useState<number | string>(opportunity.availabilityWeightPercent ?? '');
  const [officeId, setOfficeId] = useState<string | null>(
    opportunity.targetOfficeLocationId ? String(opportunity.targetOfficeLocationId) : null,
  );

  const updateMutation = useUpdateOpportunity(opportunity.id);

  const { data: officeLocations } = useQuery({
    queryKey: ['officeLocations'],
    queryFn: () => officeLocationSvc.getOfficeLocationsDictionary(),
    staleTime: 15 * 60 * 1000,
  });

  const officeOptions = (officeLocations ?? []).map((o: { id: number; name: string }) => ({
    value: String(o.id),
    label: o.name,
  }));

  const handleCancel = () => {
    setTitle(opportunity.title);
    setDescription(opportunity.description ?? '');
    setType(String(opportunity.opportunityType));
    setStatus(String(opportunity.opportunityStatus));
    setAvailDate(opportunity.requiredAvailabilityDate ? new Date(opportunity.requiredAvailabilityDate) : null);
    setCommitment(opportunity.timeCommitmentPercent ?? '');
    setAvailWeight(opportunity.availabilityWeightPercent ?? '');
    setOfficeId(opportunity.targetOfficeLocationId ? String(opportunity.targetOfficeLocationId) : null);
    setEditing(false);
  };

  const handleSave = () => {
    const payload: UpdateOpportunityRequest = {
      title: title.trim(),
      description: description.trim() || null,
      opportunityType: Number(type) as OpportunityType,
      opportunityStatus: Number(status) as OpportunityStatus,
      requiredAvailabilityDate: availDate ? availDate.toISOString().split('T')[0] : null,
      timeCommitmentPercent: commitment !== '' ? Number(commitment) : null,
      availabilityWeightPercent: availWeight !== '' ? Number(availWeight) : null,
      targetOfficeLocationId: officeId ? Number(officeId) : null,
    };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        notifications.show({ title: 'Saved', message: 'Opportunity updated.', color: 'sopraGreen' });
        setEditing(false);
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Failed to save changes.', color: 'red' });
      },
    });
  };

  if (!editing) {
    return (
      <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-brand-2)' }}>
        {opportunity.description && (
          <Text size="sm" mb="sm" style={{ whiteSpace: 'pre-wrap' }}>{opportunity.description}</Text>
        )}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="xl" wrap="wrap" style={{ flex: 1 }}>
            <Box>
              <Text fw={500} size="xs" c="dimmed">Type</Text>
              <Text size="sm">{TYPE_LABEL[opportunity.opportunityType]}</Text>
            </Box>
            {opportunity.timeCommitmentPercent != null && (
              <Box>
                <Text fw={500} size="xs" c="dimmed">Commitment</Text>
                <Text size="sm">{opportunity.timeCommitmentPercent}%</Text>
              </Box>
            )}
            {opportunity.targetOfficeName && (
              <Box>
                <Text fw={500} size="xs" c="dimmed">Office</Text>
                <Text size="sm">{opportunity.targetOfficeName}</Text>
              </Box>
            )}
            {opportunity.requiredAvailabilityDate && (
              <Box>
                <Text fw={500} size="xs" c="dimmed">Available from</Text>
                <Text size="sm">{opportunity.requiredAvailabilityDate}</Text>
              </Box>
            )}
            {opportunity.availabilityWeightPercent != null && (
              <Box>
                <Text fw={500} size="xs" c="dimmed">Availability weight</Text>
                <Text size="sm">{opportunity.availabilityWeightPercent}%</Text>
              </Box>
            )}
            <Box>
              <Text fw={500} size="xs" c="dimmed">Created by</Text>
              <Text size="sm">{createdBy}</Text>
            </Box>
            <Box>
              <Text fw={500} size="xs" c="dimmed">Created at</Text>
              <Text size="sm">{new Date(opportunity.createdAt).toLocaleDateString()}</Text>
            </Box>
          </Group>
          {!isArchived && (
            <ActionIcon variant="subtle" color="gray" onClick={() => setEditing(true)} aria-label="Edit details" style={{ flexShrink: 0 }}>
              <PencilIcon style={{ width: 14, height: 14 }} />
            </ActionIcon>
          )}
        </Group>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-brand-2)' }}>
      <Stack gap="sm">
        <TextInput
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          size="sm"
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          size="sm"
          autosize
          minRows={2}
          maxRows={6}
        />
        <Group gap="sm" wrap="wrap">
          <Select
            label="Type"
            data={TYPE_OPTIONS}
            value={type}
            onChange={(v) => v && setType(v)}
            size="sm"
            w={160}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => v && setStatus(v)}
            size="sm"
            w={140}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label="Office"
            data={officeOptions}
            value={officeId}
            onChange={setOfficeId}
            clearable
            size="sm"
            w={180}
            comboboxProps={{ withinPortal: true }}
          />
          <DateInput
            label="Available from"
            value={availDate}
            onChange={setAvailDate}
            clearable
            size="sm"
            w={160}
            valueFormat="YYYY-MM-DD"
            popoverProps={{ withinPortal: true }}
          />
          <NumberInput
            label="Commitment %"
            value={commitment}
            onChange={setCommitment}
            min={0}
            max={100}
            suffix="%"
            size="sm"
            w={130}
          />
          <NumberInput
            label="Availability weight %"
            value={availWeight}
            onChange={setAvailWeight}
            min={0}
            max={100}
            suffix="%"
            size="sm"
            w={170}
          />
        </Group>
        <Group gap="xs">
          <Button size="xs" variant="filled" color="brand.5" onClick={handleSave} loading={updateMutation.isPending}>
            Save
          </Button>
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={handleCancel} disabled={updateMutation.isPending} aria-label="Cancel">
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </ActionIcon>
        </Group>
      </Stack>
    </Paper>
  );
};
